import { Shield, Users, Lock, FileText, Database, AlertCircle } from 'lucide-react';

const Security = () => {
  return (
    <div className="pt-24 pb-20">
      <div className="container mx-auto">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold heading-font text-white mb-6">
            Security & Compliance
          </h1>
          <p className="text-xl text-gray-400 leading-relaxed">
            Built with enterprise-grade controls to protect your business spending and financial data.
          </p>
        </div>

        {/* Security Features */}
        <div className="max-w-5xl mx-auto space-y-8 mb-20">
          <div className="bg-white/5 rounded-2xl border border-white/10 p-8 backdrop-blur-sm">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-indigo-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Users size={24} className="text-indigo-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold heading-font text-white mb-3">
                  Business & User Verification
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  All businesses undergo verification (KYB) to ensure legitimacy and compliance. User identity verification is applied as required by our financial institution partners and regulatory requirements.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-2xl border border-white/10 p-8 backdrop-blur-sm">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-indigo-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Lock size={24} className="text-indigo-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold heading-font text-white mb-3">
                  Role-Based Access & Approvals
                </h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Control who can view transactions, issue cards, and approve spending. Set up multi-level approval workflows for large purchases and ensure proper authorization at every step.
                </p>
                <ul className="space-y-2 text-gray-400">
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-400 mt-1">•</span>
                    <span>Admin, manager, and employee roles with granular permissions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-400 mt-1">•</span>
                    <span>Approval requirements for spend above defined thresholds</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-400 mt-1">•</span>
                    <span>Team-level visibility controls for sensitive transactions</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-2xl border border-white/10 p-8 backdrop-blur-sm">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-indigo-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield size={24} className="text-indigo-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold heading-font text-white mb-3">
                  Spend Controls
                </h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Prevent unauthorized spending with real-time controls that enforce your policies automatically.
                </p>
                <ul className="space-y-2 text-gray-400">
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-400 mt-1">•</span>
                    <span>Daily, weekly, and monthly spending limits per card</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-400 mt-1">•</span>
                    <span>Merchant category restrictions (block/allow specific types)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-400 mt-1">•</span>
                    <span>Individual merchant controls</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-400 mt-1">•</span>
                    <span>Instant freeze/unfreeze for any card</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-2xl border border-white/10 p-8 backdrop-blur-sm">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-indigo-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <FileText size={24} className="text-indigo-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold heading-font text-white mb-3">
                  Audit Log & Exports
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  Complete visibility into all account activity with immutable audit trails. Every action—card creation, spending, approvals, and policy changes—is logged with timestamps and user attribution. Export clean CSV records with all transaction details, receipts, and notes for accounting and compliance needs.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-2xl border border-white/10 p-8 backdrop-blur-sm">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-indigo-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Database size={24} className="text-indigo-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold heading-font text-white mb-3">
                  Data Protection
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  Your financial data is protected with industry-standard safeguards including encryption in transit and at rest. We follow security best practices and undergo regular security assessments to ensure your data remains safe.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-2xl border border-white/10 p-8 backdrop-blur-sm">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-indigo-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <AlertCircle size={24} className="text-indigo-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold heading-font text-white mb-3">
                  Responsible Disclosure
                </h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  We take security vulnerabilities seriously. If you discover a security issue, please report it responsibly.
                </p>
                <div className="mt-4 p-4 bg-white/5 rounded-lg border border-white/10">
                  <p className="text-sm text-gray-400 mb-2">Security contact:</p>
                  <a 
                    href="mailto:security@limitly.org" 
                    className="text-indigo-400 hover:text-indigo-300 transition-colors font-medium"
                  >
                    security@limitly.org
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-gradient-to-br from-indigo-600/20 to-cyan-600/20 rounded-2xl border border-indigo-600/30 p-8">
            <h3 className="text-2xl font-bold heading-font text-white mb-4">
              Questions about security?
            </h3>
            <p className="text-gray-300 mb-6">
              Our team is here to help you understand how Limitly protects your business.
            </p>
            <a
              href="/contact"
              className="inline-block px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
            >
              Contact us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Security;
